/** SA-53: link-only CRM contact search for the manual-order modal.
 *  SA-188: + no-match "create new contact" mini-form (resolve-or-create
 *  POST /api/crm/contacts, then linked through the same onSelect path). */

import { useEffect, useRef, useState } from 'react'
import { Button, Input } from '@/components/ui'
import { apiClient } from '@/services/apiClient'

export interface ContactHit {
  id: string
  display_name: string | null
  channel: string
  channel_user_id: string
}

const DIGITS_LIKE = /^[\d\s()+.-]{5,}$/

export function ContactSearch({ onSelect }: { onSelect: (c: ContactHit) => void }) {
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<ContactHit[]>([])
  // Latched: a 403 means the caller lacks crm:read, which won't change for
  // the life of this mount — hide the control instead of leaving a
  // permanently-dead search box.
  const [hidden, setHidden] = useState(false)
  // Create affordance shows only after a completed search found nothing.
  const [noMatch, setNoMatch] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [createErr, setCreateErr] = useState(false)
  const [busy, setBusy] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    clearTimeout(timer.current)
    const q = query.trim()
    setNoMatch(false)
    if (q.length < 2) { setHits([]); return }
    timer.current = setTimeout(async () => {
      try {
        const res = await apiClient.fetch(
          `/api/crm/contacts?search=${encodeURIComponent(q)}&per_page=8`)
        if (!res.ok) {
          if (res.status === 403) { setHidden(true); return }
          setHits([])
          return
        }
        const data = (await res.json()) as { contacts: ContactHit[] }
        setHits(data.contacts)
        setNoMatch(data.contacts.length === 0)
      } catch { setHits([]) /* link is optional — degrade silently */ }
    }, 300)
    return () => clearTimeout(timer.current)
  }, [query])

  if (hidden) return null

  const reset = () => {
    setQuery(''); setHits([]); setNoMatch(false); setCreating(false)
    setNewName(''); setNewPhone(''); setCreateErr(false)
  }

  const openMiniForm = () => {
    const q = query.trim()
    if (DIGITS_LIKE.test(q)) { setNewPhone(q); setNewName('') }
    else { setNewName(q); setNewPhone('') }
    setCreateErr(false)
    setCreating(true)
  }

  const createAndLink = async () => {
    const name = newName.trim()
    if (!name || busy) return
    setBusy(true); setCreateErr(false)
    try {
      const phone = newPhone.trim()
      const created = await apiClient.post<ContactHit>('/api/crm/contacts',
        { display_name: name, ...(phone ? { phone } : {}) })
      onSelect(created)
      reset()
    } catch {
      setCreateErr(true)  // explicit action — never degrade silently
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-1">
      <Input placeholder="Search contacts" aria-label="Search contacts"
        value={query} onChange={e => setQuery(e.target.value)} />
      {hits.length > 0 && !creating && (
        <ul className="rounded-lg border border-border max-h-40 overflow-y-auto">
          {hits.map(c => (
            <li key={c.id}>
              <button type="button"
                onClick={() => { onSelect(c); setQuery(''); setHits([]) }}
                className="w-full flex justify-between px-2 py-1.5 text-sm rounded hover:bg-muted text-left">
                <span>{c.display_name || c.channel_user_id}</span>
                <span className="text-muted-foreground">{c.channel_user_id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {hits.length === 0 && noMatch && !creating && (
        <button type="button" onClick={openMiniForm}
          className="w-full px-2 py-1.5 text-sm rounded-lg border border-dashed border-border text-left hover:bg-muted">
          + Create contact "{query.trim()}"
        </button>
      )}
      {creating && (
        <div className="space-y-2 rounded-lg border border-border p-2">
          <div className="grid grid-cols-2 gap-2">
            <Input aria-label="New contact name" placeholder="Name"
              value={newName} onChange={e => setNewName(e.target.value)} />
            <Input aria-label="New contact phone" placeholder="Phone"
              value={newPhone} onChange={e => setNewPhone(e.target.value)} />
          </div>
          {createErr && (
            <p className="text-sm text-destructive">Could not create contact — try again.</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm"
              onClick={() => setCreating(false)} disabled={busy}
              className="text-muted-foreground hover:text-foreground">
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={createAndLink}
              disabled={!newName.trim() || busy}>
              Create &amp; link
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
